import { useTranslation } from 'next-i18next/pages';
import React, { useEffect, useRef } from 'react';

import { PRODUCTION_RAIL_STYLE } from './productionRailStyles';
import { type BoxVariant, IsoBox } from './railActors';
import { InspectionZone, SignalPath } from './railInspection';
import { SCENE, SCENE_FOCUS, SERVER, STATIONS, capsuleX } from './railLayout';
import { Microphone, RobotArm } from './railMachines';
import { INK, INK_SOFT, PAPER } from './railPalette';
import { AiServer, ConveyorBelt } from './railStructures';
import { BOX_COUNT, DEFECT_INDICES, boxDelaySeconds } from './railTiming';

/**
 * The boxes on the shared loop, spaced evenly by negative delays so the belt
 * is already populated on first paint. Which of them are defects is decided
 * with the timing, so the picker and the server act at the right moments.
 */
const BOXES: ReadonlyArray<{ variant: BoxVariant; delaySeconds: number }> =
  Array.from({ length: BOX_COUNT }, (_, index) => ({
    variant: DEFECT_INDICES.includes(index) ? 'defect' : 'normal',
    delaySeconds: boxDelaySeconds(index),
  }));

const CHIP_FONT_SIZE = 17;

/**
 * Width a caption needs at the chip's size, from its characters: a Hangul or
 * CJK glyph is about as wide as it is tall, Latin about half, plus padding.
 * SVG text cannot size its own box, and a chip that hugs its text in one
 * language and swallows it in the other is worse than an estimate.
 */
function estimateChipWidth(text: string): number {
  const width = Array.from(text).reduce(
    (sum, char) => sum + (/[ᄀ-ᇿ㄰-㆏가-힯一-鿿]/.test(char) ? 1 : 0.56),
    0,
  );

  return Math.round(width * CHIP_FONT_SIZE + 36);
}

interface LabelChipProps {
  x: number;
  y: number;
  children: string;
}

/**
 * A caption in an outlined chip, centred on the point it names. The chip is
 * filled with the background so the line behind it does not run through the
 * text. Hidden below the sm breakpoint, where the scene is scaled past
 * legibility.
 */
function LabelChip({ x, y, children }: LabelChipProps) {
  const width = estimateChipWidth(children);

  return (
    <g>
      <rect
        x={x - width / 2}
        y={y - 18}
        width={width}
        height={36}
        rx={6}
        fill={PAPER}
        stroke={INK_SOFT}
      />
      <text
        x={x}
        y={y + 6}
        textAnchor="middle"
        fontSize={CHIP_FONT_SIZE}
        fontWeight={600}
        fill={INK}
      >
        {children}
      </text>
    </g>
  );
}

/**
 * ### ProductionRailIllustration
 *
 * The brochure's production line as line art across the full width of the
 * page: the belt runs in from the left edge of any screen and out past the
 * right, units ride it under the boom microphone, and the analysis server
 * stands at the end of the line. Every unit stops on the plate under the
 * microphone and is turned once to be heard all round; when a defect has
 * been heard, the server's badge turns from OK to NG and the picker lifts
 * that box clear of the line. The good units carry on.
 *
 * The scene is sized by height, so the belt simply continues to both edges
 * of any screen and only how much of the line is in frame changes. A wide
 * screen holds the whole drawing and centres it. A narrow one cannot, and
 * there the frame is hung on the story rather than on the drawing's centre:
 * centred, a phone would keep the microphone and the picker and push the
 * server off the right-hand edge, taking the verdict with it. The scene is
 * also set shorter on a phone, since the frame has to be wide enough in
 * scene units to hold the microphone and the server at once.
 *
 * It illustrates the claim below it and states nothing of its own, so it is
 * hidden from assistive technology.
 *
 * @returns The rendered illustration element
 */
export default function ProductionRailIllustration() {
  const { t } = useTranslation('common');
  const [station] = STATIONS;
  const svgRef = useRef<SVGSVGElement>(null);

  /*
   * Every movement in the drawing is timed against the others: the disc
   * rises as the box arrives, the picker comes down as the defect stops.
   * That only holds if every animation is started from one moment, and a
   * browser starts a CSS animation whenever it first styles the element,
   * which is not one moment when parts of the drawing are mounted or
   * re-mounted apart from the rest. So once mounted, every animation in
   * the drawing is put on the same start time.
   */
  useEffect(() => {
    const svg = svgRef.current;
    if (svg === null) return;
    const startTime = document.timeline.currentTime;
    for (const animation of svg.getAnimations({ subtree: true })) {
      animation.startTime = startTime;
    }
  }, []);

  return (
    <div
      className="relative h-[240px] w-full overflow-hidden [container-type:size] sm:h-[380px] lg:h-[560px]"
      aria-hidden="true"
    >
      {/*
       * The drawing's left edge sits at the middle of the screen and is
       * pulled back by this share of its own width, which lands that point
       * of the scene on the middle of the screen. At lg the whole drawing is
       * in frame and it is simply centred. The wrapper is a size container
       * so the drawing's stylesheet can read the frame's height and keep
       * the screen-width strokes on the boxes in step with the scale.
       */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SCENE.width} ${SCENE.height}`}
        className="absolute left-1/2 top-0 h-full w-auto -translate-x-[var(--scene-focus)] overflow-visible aspect-[1200/480] lg:-translate-x-1/2"
        style={
          {
            '--scene-focus': `${(SCENE_FOCUS * 100).toFixed(2)}%`,
          } as React.CSSProperties
        }
        focusable="false"
      >
        <style>{PRODUCTION_RAIL_STYLE}</style>

        <SignalPath />
        <Microphone offsetX={station.offsetX} mirrored={station.mirrored} />
        <RobotArm />
        <ConveyorBelt />
        <InspectionZone />

        {BOXES.map(({ variant, delaySeconds }) => (
          <IsoBox
            key={delaySeconds}
            variant={variant}
            delaySeconds={delaySeconds}
          />
        ))}

        {/* After the boxes, so they pass behind the server on their way out */}
        <AiServer />

        <g className="hidden sm:block">
          <LabelChip x={capsuleX(station) - 60} y={404}>
            {t('품질검사 구간 (불량 검출)')}
          </LabelChip>
          <LabelChip x={SERVER.x + SERVER.width / 2} y={456}>
            {t('AI 분석 서버')}
          </LabelChip>
        </g>
      </svg>
    </div>
  );
}
